// Document type enum
export type DocumentType = 'invoice' | 'receipt' | 'credit_note' | 'other'

// Document type labels for display
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  invoice: 'Invoice',
  receipt: 'Receipt',
  credit_note: 'Credit Note',
  other: 'Other',
}

// Hardcoded UUID → document type name mapping
const DOCUMENT_TYPE_ID_MAP: Record<string, DocumentType> = {
  '919c6811-3b31-4d59-9c3c-068d3446b9a3': 'invoice',
  '975df803-2347-4c82-a18b-d31d098fa0f1': 'receipt',
}

// Helper to get display label for a document type
// Resolves from document_type name string, or falls back to document_type_id UUID lookup
export function getDocumentTypeLabel(
  type: string | null | undefined,
  typeId?: string | null
): string {
  // Try resolving from name string first
  if (type) {
    const normalized = type.toLowerCase() as DocumentType
    if (DOCUMENT_TYPE_LABELS[normalized]) return DOCUMENT_TYPE_LABELS[normalized]
    return type.replace('_', ' ')
  }
  // Fall back to UUID lookup
  if (typeId) {
    const typeName = DOCUMENT_TYPE_ID_MAP[typeId]
    if (typeName) return DOCUMENT_TYPE_LABELS[typeName]
  }
  return 'Unknown'
}

// Document status enum
export type DocumentStatus =
  | 'new'
  | 'processing'
  | 'extracted'
  | 'validated'
  | 'reported'
  | 'invalid'
  | 'duplicate'
  | 'manual_review'

// Relationship type enum
export type RelationshipType =
  | 'invoice_receipt'
  | 'credit_note'
  | 'amendment'
  | 'duplicate'
  | 'related'

// Document interface
export interface Document {
  id: string
  business_id: string
  document_type: string
  document_type_id: string | null
  document_status: DocumentStatus
  document_date: string
  document_number: string | null
  issued_by: string | null
  issued_to: string | null
  value: number | null
  vat_value: number | null
  value_in_ils: number | null
  original_currency: string
  s3_key: string
  checksum: string
  source: string
  extracted_metadata: Record<string, unknown> | null
  sumit_status?: string | null
  sumit_comment?: string | null
  sumit_folder_id?: string | null
  sumit_document_id?: string | null
  created_at: string
  updated_at: string
}

// Business interface
export interface Business {
  id: string
  name: string
  accounting_identifier: string | null
  configuration: Record<string, unknown> | null
  alternative_names: string[]
  credit_card_endings: string[]
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

// Document relationship interface
export interface DocumentRelationship {
  id: string
  document_id: string
  related_document_id: string
  relationship_type: RelationshipType
  confidence_score: number
}

// API request/response types
export interface ListDocumentsParams {
  business_id: string
  document_type?: string
  document_status?: string
  sumit_status?: string
  date_from?: string
  date_to?: string
  limit?: number
  offset?: number
}

export interface ListDocumentsResponse {
  documents: Document[]
  total: number
  limit: number
  offset: number
}

export interface ListBusinessesResponse {
  businesses: Business[]
  total: number
  limit: number
  offset: number
}

// Business API request/response types
export interface ListBusinessesParams {
  limit?: number
  offset?: number
}

export interface CreateBusinessData {
  name: string
  accounting_identifier?: string
  alternative_names?: string[]
  credit_card_endings?: string[]
  metadata?: Record<string, unknown>
}

export interface UpdateBusinessData {
  name?: string
  accounting_identifier?: string
  alternative_names?: string[]
  credit_card_endings?: string[]
  metadata?: Record<string, unknown>
}

export interface ApiErrorResponse {
  error: {
    code: string
    message: string
  }
}

// Document filters for UI
export interface DocumentFilters {
  documentType?: string
  documentStatus?: string
  sumitStatus?: string
  dateFrom?: string
  dateTo?: string
}

// Monthly group for reporting
export interface MonthlyGroup {
  month: string // YYYY-MM format
  documents: Document[]
  totalValue: number
  totalVat: number
  count: number
  isExpanded: boolean
}

// Supplier interface
export interface Supplier {
  id: string
  business_id: string
  name: string
  alternative_names: string[]
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

// Supplier API request/response types
export interface ListSuppliersParams {
  business_id: string
  limit?: number
  offset?: number
}

export interface ListSuppliersResponse {
  suppliers: Supplier[]
  total: number
  limit: number
  offset: number
}

export interface UpdateSupplierData {
  name?: string
  alternative_names?: string[]
  metadata?: Record<string, unknown>
}

export interface CreateSupplierData {
  business_id: string
  name: string
  alternative_names?: string[]
}

export interface CreateSupplierResponse extends Supplier {
  created: boolean
}

// Prompt interface
export interface Prompt {
  id: string
  name: string
  use_case: string
  prompt_text: string
  model_id: string
  is_active: boolean
  created_at: string
  updated_at: string
}

// Prompt API request/response types
export interface ListPromptsParams {
  use_case?: string
  limit?: number
  offset?: number
}

export interface ListPromptsResponse {
  prompts: Prompt[]
  total: number
  limit: number
  offset: number
}

export interface CreatePromptData {
  name: string
  use_case: string
  prompt_text: string
  model_id: string
  is_active?: boolean
}

export interface UpdatePromptData {
  name?: string
  use_case?: string
  prompt_text?: string
  model_id?: string
}
